import React from 'react';
import { Typography, Spin, Avatar, Tag } from 'antd';
import { Trophy, ChevronDown, ChevronUp, Check, Pencil } from 'lucide-react';
import { tokens } from '../theme/tokens';
import { goFullReload } from '../utils/nav';
import { SumarPostsModal } from '../components/sumar-posts-modal';
import { isPublishable } from '../types/social';

const { Title, Text } = Typography;
const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

interface RankingAccount {
  platform: string;
  social_id: string;
  username: string | null;
  avatar_url: string | null;
  points: number;
}

interface RankingEntry {
  rank: number;
  username: string;
  avatar_url: string | null;
  points: number;
  accounts: RankingAccount[];
}

interface PublishTask {
  _id: string;
  platform: string;
  status: string;
  thumbnail_url?: string;
  result_url: string | null;
  caption: string;
  created_at: string;
  platform_post_id: string | null;
}

const PLATFORM_COLOR: Record<string, string> = {
  instagram: '#E1306C',
  tiktok: '#010101',
};

const MEDAL: Record<number, string> = { 1: '🥇', 2: '🥈', 3: '🥉' };

// ── Per-entry row: header is the existing card; click expands to show the
//    last 5 publications across all of this user's accounts (newest first).
//    Fetch is lazy (only on first expand) and aborts on unmount/collapse.
const RankingRow: React.FC<{ entry: RankingEntry }> = ({ entry }) => {
  const [expanded,     setExpanded]     = React.useState(false);
  const [tasks,        setTasks]        = React.useState<PublishTask[] | null>(null);
  const [loadingTasks, setLoadingTasks] = React.useState(false);

  React.useEffect(() => {
    if (!expanded || tasks !== null) return;
    if (entry.accounts.length === 0) { setTasks([]); return; }

    setLoadingTasks(true);
    const ac = new AbortController();
    let cancelled = false;

    Promise.all(
      entry.accounts.map(acc =>
        fetch(`${API_BASE}/drupi/publish-tasks?social_id=${acc.social_id}&limit=5`, { signal: ac.signal })
          .then(r => r.ok ? r.json() as Promise<PublishTask[]> : [])
          .catch(() => [] as PublishTask[])
      )
    ).then(results => {
      if (cancelled) return;
      const merged = results.flat()
        .filter(t => t.status === 'done' && t.thumbnail_url && t.result_url)
        .sort((a, b) => b.created_at.localeCompare(a.created_at))
        .slice(0, 5);
      setTasks(merged);
      setLoadingTasks(false);
    });

    return () => { cancelled = true; ac.abort(); };
  }, [expanded, tasks, entry.accounts]);

  return (
    <div
      style={{
        background: entry.rank <= 3 ? 'rgba(51, 63, 131, 0.04)' : '#f9fafb',
        border: `1px solid ${entry.rank <= 3 ? 'rgba(51, 63, 131, 0.18)' : 'rgba(0,0,0,0.08)'}`,
        borderRadius: 12,
      }}
    >
      {/* Header (click to toggle) */}
      <button
        type='button'
        onClick={() => setExpanded(v => !v)}
        style={{
          all: 'unset',
          width: '100%',
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          padding: '14px 20px',
          cursor: 'pointer',
        }}
      >
        {/* Rank */}
        <div style={{ width: 36, textAlign: 'center', flexShrink: 0 }}>
          {MEDAL[entry.rank] ? (
            <span style={{ fontSize: 22 }}>{MEDAL[entry.rank]}</span>
          ) : (
            <Text style={{ color: tokens.colors.primary.neutral[500], fontWeight: 700, fontSize: 15 }}>
              #{entry.rank}
            </Text>
          )}
        </div>

        {/* Avatar */}
        <Avatar
          src={entry.avatar_url || undefined}
          size={42}
          style={{ background: tokens.colors.brand.gradient, flexShrink: 0 }}
        >
          {(entry.username || '?').charAt(0).toUpperCase()}
        </Avatar>

        {/* Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text
            style={{
              color: tokens.colors.primary.neutral[900],
              fontWeight: 600,
              fontSize: 15,
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            @{entry.username}
          </Text>
          <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
            {entry.accounts.map(acc => (
              <Tag
                key={`${acc.platform}:${acc.social_id}`}
                style={{
                  background: `${PLATFORM_COLOR[acc.platform] || '#888'}22`,
                  border: `1px solid ${PLATFORM_COLOR[acc.platform] || '#888'}55`,
                  color: PLATFORM_COLOR[acc.platform] || '#888',
                  fontSize: 10,
                  fontWeight: 600,
                  borderRadius: 6,
                  margin: 0,
                  lineHeight: '18px',
                }}
              >
                {acc.platform}
              </Tag>
            ))}
          </div>
        </div>

        {/* Points + chevron */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <div style={{ textAlign: 'right' }}>
            <Text
              style={{
                fontWeight: 800,
                fontSize: 20,
                background: tokens.colors.brand.gradient,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {entry.points.toLocaleString()}
            </Text>
            <Text style={{ display: 'block', color: tokens.colors.primary.neutral[500], fontSize: 11 }}>
              pts
            </Text>
          </div>
          {expanded
            ? <ChevronUp   size={18} color={tokens.colors.primary.neutral[400]} />
            : <ChevronDown size={18} color={tokens.colors.primary.neutral[400]} />
          }
        </div>
      </button>

      {/* Expanded panel — last 5 publications */}
      {expanded && (
        <div style={{
          padding: '0 20px 16px',
          borderTop: '1px solid rgba(0,0,0,0.06)',
          marginTop: -1,
        }}>
          {loadingTasks ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <Spin size='small' />
            </div>
          ) : !tasks || tasks.length === 0 ? (
            <Text style={{ display: 'block', textAlign: 'center', padding: '16px 0', color: tokens.colors.primary.neutral[400], fontSize: 13 }}>
              Sin publicaciones recientes
            </Text>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(5, 1fr)',
              gap: 8,
              paddingTop: 14,
            }}>
              {tasks.map(task => (
                <a
                  key={task._id}
                  href={task.result_url!}
                  target='_blank'
                  rel='noopener noreferrer'
                  title={task.caption || undefined}
                  style={{
                    display: 'block',
                    position: 'relative',
                    paddingTop: '177.78%',  // 9:16
                    borderRadius: 8,
                    overflow: 'hidden',
                    background: '#000',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                  }}
                >
                  <img
                    src={task.thumbnail_url}
                    alt=''
                    loading='lazy'
                    style={{
                      position: 'absolute',
                      top: 0, left: 0,
                      width: '100%', height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                </a>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const JWT_KEY        = 'drupi_social_jwt';
const MILESTONE_PTS  = 100_000;   // 100k pts = US$100
const MAX_MILESTONES = 5;         // tope 500k → US$500

// Fecha (HARDCODEADA) a partir de la cual se "encienden" los premios y mostramos el
// dashboard con los montos en dólares. Hasta esa fecha se muestra SÓLO el teaser (sin
// plata). Puesta MUY a futuro a propósito: el dashboard queda OCULTO hasta que hagamos
// el deploy de go-live. Cuando arranquen los premios de verdad, poné acá la fecha real
// y deployá. OJO: el mes es 0-indexed.
const PRIZES_LIVE_FROM = new Date(2027, 0, 1);

// Colores de la escalera: amarillo → verde (izq → der), como el diseño.
const LADDER_COLORS = ['#FFC93C', '#E4C13A', '#9FD14A', '#57C15A', '#22C55E'];

// Nombre a mostrar en el hero: preferimos el display_name real; NUNCA el open_id
// (que se guarda en social_id y a veces cae como username). Fallback al @handle.
function milestoneHeroName(acc: any, entry?: RankingEntry): string {
  if (acc?.username && acc.username !== acc.social_id) return acc.username;
  if (acc?.tiktok_handle) return `@${acc.tiktok_handle}`;
  if (entry?.username && entry.username !== acc?.social_id) return entry.username;
  return 'Tu perfil';
}

// ─── Hero de progreso personal (arriba del ranking) ──────────────────────────
// Suma los `points` de las cuentas del usuario logueado (= puntos del mes) y los
// mapea a la escalera de premios: cada 100k pts = US$100, tope US$500. El puesto
// (medalla) sale de cruzar las cuentas contra el `entries` del ranking de abajo.
// Sólo se muestra si hay JWT (usuario logueado); best-effort, no rompe el ranking.
const MilestoneProgress: React.FC<{ entries: RankingEntry[] }> = ({ entries }) => {
  const [accounts, setAccounts] = React.useState<any[] | null>(null);
  const [sumarOpen, setSumarOpen] = React.useState(false);
  // Nombre a mostrar en el hero: si el usuario cargó su nombre en el perfil (Ajustes →
  // Datos Personales), preferimos ESE por sobre el display_name de TikTok. Se fetchea acá
  // directo (no dependemos del PaymentProfileProvider) para no acoplar este módulo.
  const [profileName, setProfileName] = React.useState('');

  React.useEffect(() => {
    const jwt = localStorage.getItem(JWT_KEY);
    if (!jwt) { setAccounts([]); return; }
    let alive = true;
    fetch(`${API_BASE}/drupi/user-profile`, { headers: { Authorization: `Bearer ${jwt}` } })
      .then(r => (r.ok ? r.json() : null))
      .then((p: any) => { if (alive && p && typeof p.full_name === 'string') setProfileName(p.full_name.trim()); })
      .catch(() => { /* best-effort: sin nombre de perfil caemos al de TikTok */ });
    return () => { alive = false; };
  }, []);

  React.useEffect(() => {
    const jwt = localStorage.getItem(JWT_KEY);
    if (!jwt) { setAccounts([]); return; }
    let alive = true;
    fetch(`${API_BASE}/drupi/accounts`, { headers: { Authorization: `Bearer ${jwt}` } })
      .then(r => (r.ok ? r.json() : []))
      .then((accs: any[]) => { if (alive) setAccounts(Array.isArray(accs) ? accs : []); })
      .catch(() => { if (alive) setAccounts([]); });
    return () => { alive = false; };
  }, []);

  if (accounts === null) return null;                       // cargando
  const live = accounts.filter(a => a && !a.disconnected);
  if (live.length === 0) return null;                       // no logueado → sin módulo

  // Flag de preview `?milestones`: adelanta el dashboard aunque los premios todavía
  // no estén "vivos" (PRIZES_LIVE_FROM). Con valor numérico (`?milestones=250000`)
  // además fuerza esos puntos, para poder ver el hero en cualquier tramo sin depender
  // de los puntos reales. Sin valor (`?milestones`) usa los puntos reales.
  const flagRaw   = typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('milestones')
    : null;
  const flagOn    = flagRaw !== null;
  const flagPts   = flagRaw && /^\d+$/.test(flagRaw) ? Number(flagRaw) : null;

  // Antes de PRIZES_LIVE_FROM los premios todavia no cuentan: ocultamos TODO el
  // dashboard (puntos, plata, escalera) y solo anunciamos que arrancan en breve.
  // El flag `?milestones` saltea este gate para poder previsualizar.
  if (new Date() < PRIZES_LIVE_FROM && !flagOn) {
    return (
      <div style={{
        borderRadius: 16, padding: '22px', marginBottom: 28, textAlign: 'center',
        background: 'linear-gradient(135deg, #fff7e6 0%, #fffbe6 100%)',
        border: '1px solid #ffe58f',
      }}>
        <div style={{ fontSize: 30, lineHeight: 1 }}>🎁</div>
        <Text style={{ display: 'block', marginTop: 8, fontSize: 17, fontWeight: 800, color: '#d48806' }}>
          En breve arrancan los premios
        </Text>
      </div>
    );
  }

  const points = flagPts != null
    ? flagPts
    : live.reduce((s, a) => s + (Number(a.points) || 0), 0);

  // Identidad + puesto: cruzamos las cuentas del usuario con el ranking de abajo.
  const socialIds = new Set(live.map(a => a.social_id));
  const myEntry   = entries.find(e => e.accounts?.some(a => socialIds.has(a.social_id)));
  const rank      = myEntry?.rank ?? null;
  const primary   = live.find(a => a.tiktok_handle) || live.find(a => a.avatar_url) || live[0];
  const avatarUrl = myEntry?.avatar_url || primary?.avatar_url || null;
  const name      = profileName || milestoneHeroName(primary, myEntry);
  const initial   = (name || '?').replace(/^@/, '').charAt(0).toUpperCase();

  // Modal "Sumar mis posts" — el MISMO que se abre en Home y Mis publicaciones.
  // Al hacer click en la tarjeta/fila se abre (si hay al menos una cuenta publicable).
  const modalAccounts = live
    .filter(a => isPublishable(a.platform))
    .map(a => ({ provider: a.platform, socialId: a.social_id, username: a.username || a.social_id }));
  const canSumar  = modalAccounts.length > 0;
  const openSumar = () => { if (canSumar) setSumarOpen(true); };
  const sumarModal = (
    <SumarPostsModal accounts={modalAccounts} open={sumarOpen} onClose={() => setSumarOpen(false)} />
  );

  // Lápiz → Ajustes (mismo control en la fila vacía y en el hero). stopPropagation
  // para que el click en el lápiz NO dispare el modal de sumar posts.
  const pencil = (
    <button
      type='button'
      onClick={(e) => { e.stopPropagation(); goFullReload('/settings'); }}
      title='Editar en Ajustes'
      aria-label='Editar en Ajustes'
      style={{ all: 'unset', cursor: 'pointer', display: 'inline-flex', padding: 3, borderRadius: 6 }}
    >
      <Pencil size={16} color='rgba(255,255,255,0.85)' />
    </button>
  );

  // Avatar (rojo con inicial, o foto). Se reusa en la fila vacía y en el hero.
  const avatar = (size: number) => (
    <div style={{
      width: size, height: size, borderRadius: '50%', overflow: 'hidden', flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: avatarUrl ? '#fff' : 'linear-gradient(135deg,#ff6b6b,#e5484d)',
    }}>
      {avatarUrl
        ? <img src={avatarUrl} alt='' style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        : <span style={{ color: '#fff', fontSize: Math.round(size * 0.42), fontWeight: 800 }}>{initial}</span>}
    </div>
  );

  // Sin puntos todavía → fila slim "sube un video para aparecer" (en vez del hero).
  // Click en la fila → abre el modal de sumar posts.
  if (points === 0) {
    return (
      <>
        <div
          onClick={openSumar}
          style={{
            display: 'flex', alignItems: 'center', gap: 14, marginBottom: 28,
            borderRadius: 16, padding: '16px 22px',
            background: 'linear-gradient(90deg,#5b2bd9 0%,#7c3aed 100%)',
            boxShadow: '0 12px 30px -14px rgba(91,43,217,0.6)',
            cursor: canSumar ? 'pointer' : 'default',
          }}
        >
          {avatar(42)}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ color: '#fff', fontSize: 16, fontWeight: 800 }}>{name}</span>
              {pencil}
              <span style={{
                fontSize: 11, fontWeight: 700, letterSpacing: 0.5, color: '#fff',
                background: 'rgba(255,255,255,0.22)', borderRadius: 6, padding: '2px 8px',
              }}>TÚ</span>
            </div>
            <Text style={{ color: 'rgba(255,255,255,0.85)', fontSize: 13, display: 'block', marginTop: 2 }}>
              ¡Sube un video para aparecer aquí!
            </Text>
          </div>
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <div style={{ color: '#fff', fontSize: 24, fontWeight: 800, lineHeight: 1 }}>0</div>
            <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: 12 }}>pts</div>
          </div>
        </div>
        {sumarModal}
      </>
    );
  }

  const milestone = Math.min(Math.floor(points / MILESTONE_PTS), MAX_MILESTONES);
  const atCap     = milestone >= MAX_MILESTONES;
  const badge     = rank == null ? null : (MEDAL[rank] || `#${rank}`);

  // Progreso [0..1] sobre TODA la escalera (0 → 500k pts). El nodo m está en m/5, así
  // que el nodo 1 (100k) NO queda pegado al inicio (hay tramo antes). Con pocos puntos
  // mostramos un mínimo visible ("chorro de energía") en vez de barra vacía.
  const rawFill = points / (MAX_MILESTONES * MILESTONE_PTS);
  const fill    = points <= 0 ? 0 : Math.min(1, Math.max(rawFill, 0.05));

  const NODE = 30;   // ø del círculo del nodo

  return (
    <>
    <div
      style={{
        position: 'relative', overflow: 'hidden', textAlign: 'center',
        borderRadius: 24, marginBottom: 28, padding: '26px 22px 24px',
        background: 'radial-gradient(130% 120% at 50% -10%, #7c6bf0 0%, #5a4bd4 45%, #4536b8 100%)',
        boxShadow: '0 18px 42px -14px rgba(69, 54, 184, 0.6)',
      }}
    >
      {/* Pill de felicitación — SOLO al llegar al tope (en progreso no hay banner). */}
      {atCap && (
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          padding: '7px 16px', borderRadius: 999, fontSize: 13, fontWeight: 700,
          background: 'linear-gradient(90deg,#FFD54A,#FFC107)', color: '#5c3d00',
          boxShadow: '0 4px 12px rgba(255,193,7,0.35)',
        }}>
          ⭐ ¡Felicitaciones! llegaste al tope mensual de puntos ⭐
        </div>
      )}

      {/* Avatar + medalla de puesto */}
      <div style={{
        position: 'relative', width: 116, height: 116,
        margin: atCap ? '18px auto 0' : '6px auto 0',
      }}>
        <div style={{
          boxSizing: 'border-box', width: 116, height: 116, borderRadius: '50%',
          border: '4px solid rgba(255,255,255,0.9)', overflow: 'hidden',
          boxShadow: '0 8px 22px rgba(0,0,0,0.28)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {avatar(108)}
        </div>
        {badge && (
          <div style={{
            position: 'absolute', bottom: -6, left: '50%', transform: 'translateX(-50%)',
            minWidth: 34, height: 34, padding: '0 8px', borderRadius: 999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'linear-gradient(135deg,#FFD75E,#F5A623)',
            border: '3px solid #4b3fbe', boxShadow: '0 3px 8px rgba(0,0,0,0.3)',
            fontSize: MEDAL[rank as number] ? 18 : 13, fontWeight: 800, color: '#5c3d00',
          }}>
            {badge}
          </div>
        )}
      </div>

      {/* Nombre + lápiz (→ Ajustes) */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 16 }}>
        <span style={{ color: '#fff', fontSize: 25, fontWeight: 800, lineHeight: 1.15 }}>{name}</span>
        {pencil}
      </div>

      {/* Puntos del mes */}
      <div style={{ marginTop: 4 }}>
        <span style={{ color: '#fff', fontSize: 42, fontWeight: 800, lineHeight: 1 }}>
          {points.toLocaleString()}
        </span>
        <span style={{ color: 'rgba(255,255,255,0.82)', fontSize: 17, fontWeight: 600, marginLeft: 6 }}>pts</span>
      </div>

      {/* Escalera: 5 nodos (nodo m en m/5 de la barra → el nodo 1 queda inset, con tramo
          antes). El relleno arranca desde el inicio (0 pts). Colores SÓLIDOS. */}
      <div style={{ maxWidth: 440, margin: '24px auto 0', padding: '0 12px' }}>
        <div style={{ position: 'relative', height: NODE + 24 }}>
          {/* Track base (sólido) */}
          <div style={{
            position: 'absolute', top: NODE / 2 - 2, left: NODE / 2, right: NODE / 2,
            height: 4, borderRadius: 4, background: '#ABA4CC',
          }} />
          {/* Track relleno (gradiente sólido), desde el inicio, ancho ∝ progreso */}
          <div style={{
            position: 'absolute', top: NODE / 2 - 2, left: NODE / 2,
            width: `calc((100% - ${NODE}px) * ${fill})`,
            height: 4, borderRadius: 4, transition: 'width .4s ease',
            background: 'linear-gradient(90deg,#FFC93C 0%,#7BD44A 60%,#22C55E 100%)',
          }} />
          {[1, 2, 3, 4, 5].map((m, i) => {
            const done = m <= milestone;
            return (
              <div key={m} style={{
                position: 'absolute', top: 0, zIndex: 1,
                left: `calc(${NODE / 2}px + (100% - ${NODE}px) * ${m / 5})`,
                transform: 'translateX(-50%)',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 7,
              }}>
                <div style={{
                  width: NODE, height: NODE, borderRadius: '50%', boxSizing: 'border-box',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: done ? LADDER_COLORS[i] : '#BDB7DB',
                  border: done ? 'none' : '2px solid #A49CC6',
                  boxShadow: done ? '0 2px 6px rgba(0,0,0,0.22)' : 'none',
                }}>
                  {done && <Check size={16} color='#fff' strokeWidth={3} />}
                </div>
                <span style={{
                  fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
                  color: done ? '#fff' : '#C9C4E6',
                }}>
                  {m * 100}k
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
    {sumarModal}
    </>
  );
};

export const RankingPage: React.FC = () => {
  const [entries, setEntries] = React.useState<RankingEntry[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    fetch(`${API_BASE}/drupi/ranking?limit=50`)
      .then(r => r.json())
      .then(data => { console.log('[Ranking] entries recibidas:', data); setEntries(data); })
      .catch(e => { console.error('[Ranking] fetch error:', e); })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div style={{ maxWidth: 680, margin: '0 auto', padding: '40px 24px' }}>
      <MilestoneProgress entries={entries} />
      <div style={{ marginBottom: 32 }}>
        <Title level={2} style={{ margin: '0 0 8px', color: tokens.colors.primary.neutral[900], fontWeight: 700 }}>
          Ranking de creadores
        </Title>
        <Text style={{ color: tokens.colors.primary.neutral[500], fontSize: 15, display: 'block' }}>
          Los que más puntos acumularon
        </Text>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <Spin size='large' />
        </div>
      ) : entries.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <Trophy size={40} color={tokens.colors.primary.neutral[400]} />
          <Text style={{ display: 'block', marginTop: 16, color: tokens.colors.primary.neutral[500] }}>
            Todavía no hay puntos registrados
          </Text>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {entries.map(entry => (
            <RankingRow key={entry.username} entry={entry} />
          ))}
        </div>
      )}
    </div>
  );
};
